/**
 * Food Sutra ERP — primary navigation tree.
 *
 * Single source of truth for the sidebar and the command palette. Every route
 * listed here exists in `router.tsx`; every route in the router that a user is
 * expected to navigate to is listed here.
 *
 * **Wording follows the requirement's §4 department list, not generic ERP
 * vocabulary.** The business says vendor, not supplier; store, not warehouse;
 * indent, not purchase request; client, not customer; pax, not quantity. A
 * demo where the presenter has to translate the menu back into the client's
 * own words is a demo that reads as somebody else's software.
 *
 * Groups follow the order work actually happens — win it, start it, run it,
 * supply it, staff it, bill it, check it — and each group's subgroups are the
 * §4 departments, so the sidebar stays navigable without twenty top-level
 * entries.
 */
import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  BadgePercent,
  Banknote,
  BarChart3,
  Bell,
  Book,
  BookOpen,
  Boxes,
  Building2,
  Calculator,
  Calendar,
  CalendarClock,
  CheckSquare,
  ChefHat,
  ClipboardCheck,
  ClipboardList,
  Contact,
  CookingPot,
  DollarSign,
  FileSignature,
  FileSpreadsheet,
  FileText,
  FileWarning,
  Gauge,
  HandCoins,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  MessageCircleQuestion,
  MessageSquareWarning,
  Network,
  Package,
  Palmtree,
  PartyPopper,
  ReceiptText,
  Scale,
  Settings,
  ShoppingCart,
  Sparkles,
  Thermometer,
  Trash2,
  TrendingUp,
  Truck,
  UserPlus,
  UserSearch,
  Users,
  UtensilsCrossed,
  Wallet,
  Warehouse,
  Wrench,
} from 'lucide-react';

export interface NavItem {
  label: string;
  to: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  module?: string;
  roles?: string[];
  /**
   * Catalogue permission required to see this item. Populated from
   * `Modules/Core/Support/PermissionCatalogue.php`. Hiding an item is a
   * convenience, never the control — the endpoint behind it is gated too.
   */
  permission?: string;
  badge?: string | number | null;
}

export interface NavGroup {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
  accent?: NavAccent;
  subgroups?: NavSubgroup[];
  description?: string;
  defaultExpanded?: boolean;
}

export const NAV: NavGroup[] = [
  {
    label: 'Management',
    icon: Gauge,
    accent: 'violet',
    defaultExpanded: true,
    description: 'The company position, the exceptions, and the questions they raise.',
    items: [
      { label: 'Home', to: '/dashboard', icon: LayoutDashboard, description: 'Company-wide dashboard.' },
      { label: 'Business flow', to: '/help', icon: Network, description: 'How the whole company works, end to end, with a link into every step.' },
      { label: 'Control tower', to: '/management/control-tower', icon: Gauge, permission: 'management.view_control_tower', description: 'Live business position for MD, VP and GM.' },
      { label: 'Exceptions', to: '/management/exceptions', icon: AlertTriangle, permission: 'management.view', description: 'Every threshold currently breached, worst first.' },
      { label: 'Management queries', to: '/management/queries', icon: MessageCircleQuestion, permission: 'management.view', description: 'Questions put to whoever owns a number.' },
      { label: 'Business health', to: '/reports/business-health', icon: Activity, permission: 'reporting.view', description: 'What is going well, what needs attention.' },
      { label: 'Approvals', to: '/approvals', icon: CheckSquare, description: 'Documents waiting on your sign-off.' },
      { label: 'Temporary access', to: '/management/temporary-access', icon: KeyRound, permission: 'management.grant_temporary_access', description: 'Extra access lent for a stated reason, expiring by itself.' },
    ],
  },
  {
    label: 'My work',
    icon: ClipboardCheck,
    accent: 'teal',
    defaultExpanded: true,
    description: "Today's jobs. Built for a phone at site.",
    items: [
      { label: 'My work today', to: '/my-work', icon: ClipboardCheck, description: "Today's jobs, built for a phone at site." },
      { label: 'Daily checklists', to: '/checklists', icon: ClipboardList, description: 'Opening, kitchen, store, dining and safety checks.' },
      { label: 'All tasks', to: '/tasks', icon: CheckSquare, description: 'Every task in the system, with its escalation level.' },
      { label: 'Notifications', to: '/notifications', icon: Bell, description: 'Alerts and mentions.' },
    ],
  },
  {
    label: 'Site Operations',
    icon: Building2,
    accent: 'sky',
    description: '§4 — Site Operations, Kitchen, Production and Stores.',
    subgroups: [
      { label: 'Site Operations', itemPaths: ['/sites', '/sites/meals', '/sites/pax-changes', '/sites/reconciliations'] },
      { label: 'Kitchen & Production', itemPaths: ['/kitchen/recipes', '/kitchen/menus', '/kitchen/production', '/kitchen/wastage'] },
      { label: 'Stores', itemPaths: ['/products', '/inventory/stock', '/inventory/movements', '/inventory/transfers'] },
    ],
    items: [
      { label: 'Sites', to: '/sites', icon: Building2, permission: 'sites.view', description: 'Client locations Food Sutra runs.' },
      { label: 'Meal service log', to: '/sites/meals', icon: UtensilsCrossed, permission: 'sites.view', description: 'Before, during and after every meal.' },
      { label: 'Pax movements', to: '/sites/pax-changes', icon: Users, permission: 'sites.view', description: "Changes in each client's accounted head-count." },
      { label: 'Month-end statements', to: '/sites/reconciliations', icon: FileSpreadsheet, permission: 'sites.view', description: 'Pax reconciliation that a client invoice is raised from.' },

      { label: 'Recipes', to: '/kitchen/recipes', icon: ChefHat, permission: 'kitchen.view', description: 'Standardised dishes with a live cost per portion.' },
      { label: 'Menus', to: '/kitchen/menus', icon: BookOpen, permission: 'kitchen.view', description: 'Cycle menus assigned to sites.' },
      { label: 'Production plans', to: '/kitchen/production', icon: CookingPot, permission: 'kitchen.view', description: 'What each kitchen cooks, for how many, at what cost.' },
      { label: 'Food wastage', to: '/kitchen/wastage', icon: Trash2, permission: 'kitchen.view', description: 'Every recorded loss, at the stage it happened.' },

      { label: 'Materials & ingredients', to: '/products', icon: Package, permission: 'product.view', description: 'Raw material, semi-finished and service items.' },
      { label: 'Stock on hand', to: '/inventory/stock', icon: Warehouse, permission: 'inventory.view', description: 'Live stock at every site store.' },
      { label: 'Stock movements', to: '/inventory/movements', icon: Boxes, permission: 'inventory.view', description: 'Receipts, issues, transfers and wastage.' },
      { label: 'Inter-site transfers', to: '/inventory/transfers', icon: ArrowLeftRight, permission: 'inventory.view', description: 'Material moving between site stores.' },
    ],
  },
  {
    label: 'Procurement',
    icon: ShoppingCart,
    accent: 'amber',
    description: '§4 — Procurement and Vendor Management.',
    subgroups: [
      { label: 'Procurement', itemPaths: ['/purchase/requests', '/purchase/orders', '/purchase/receipts', '/purchase/bills'] },
      { label: 'Vendor Management', itemPaths: ['/masters/suppliers'] },
    ],
    items: [
      { label: 'Indents', to: '/purchase/requests', icon: ClipboardList, permission: 'purchase.view', description: 'Site requests awaiting approval.' },
      { label: 'Purchase orders', to: '/purchase/orders', icon: ShoppingCart, permission: 'purchase.view', description: 'Approved POs, with the six buying controls.' },
      { label: 'Goods receipt', to: '/purchase/receipts', icon: Truck, permission: 'purchase.view', description: 'What arrived at which store, and what was rejected.' },
      { label: 'Vendor bills', to: '/purchase/bills', icon: ReceiptText, permission: 'purchase.view', description: 'Bills, payments and vendor ageing.' },
      { label: 'Vendors', to: '/masters/suppliers', icon: Contact, permission: 'masterdata.view', description: 'Vendor master, rates and scorecards.' },
    ],
  },
  {
    label: 'People',
    icon: Users,
    accent: 'emerald',
    description: '§4 — HR, Manpower, and Attendance & Payroll.',
    subgroups: [
      { label: 'HR', itemPaths: ['/hr/dashboard', '/hr/employees', '/hr/advances', '/hr/recoveries', '/hr/exits', '/hr/wage-rules'] },
      { label: 'Manpower', itemPaths: ['/manpower/board', '/manpower/requisitions', '/manpower/reserve-pool'] },
      { label: 'Attendance & Payroll', itemPaths: ['/attendance/dashboard', '/leave/dashboard', '/payroll/dashboard'] },
    ],
    items: [
      { label: 'HR overview', to: '/hr/dashboard', icon: Gauge, permission: 'hr.view', description: 'Joiners, exits and missing documents.' },
      { label: 'Employees', to: '/hr/employees', icon: Users, permission: 'hr.view', description: 'Employee master with wages and documents.' },
      { label: 'Advances', to: '/hr/advances', icon: HandCoins, permission: 'hr.view', description: 'Advances paid and the recovery running against each.' },
      { label: 'Recoveries', to: '/hr/recoveries', icon: Wallet, permission: 'hr.view', description: 'Everything owed by staff — advance, loan, uniform, ID card — in one place.' },
      { label: 'Exits & full and final', to: '/hr/exits', icon: LogOut, permission: 'hr.view', description: 'Leavers, clearances and settlements.' },
      { label: 'Wage rules', to: '/hr/wage-rules', icon: Scale, permission: 'hr.view', description: 'Government minimum-wage notifications by state.' },

      { label: 'Manpower board', to: '/manpower/board', icon: UserSearch, permission: 'manpower.view', description: 'Required vs assigned vs present, per site.' },
      { label: 'Requisitions', to: '/manpower/requisitions', icon: UserPlus, permission: 'manpower.view', description: 'Sites and events asking for people.' },
      { label: 'Reserve pool', to: '/manpower/reserve-pool', icon: Users, permission: 'manpower.view', description: 'Trained staff on the bench.' },

      { label: 'Attendance', to: '/attendance/dashboard', icon: Calendar, permission: 'attendance.view', description: 'Attendance, overtime and regularisation.' },
      { label: 'Leave', to: '/leave/dashboard', icon: Palmtree, permission: 'leave.view', description: 'Leave balances, requests and calendar.' },
      { label: 'Payroll', to: '/payroll/dashboard', icon: HandCoins, permission: 'payroll.view', description: 'Salary runs, statutory and payslips.' },
    ],
  },
  {
    label: 'Business Development',
    icon: PartyPopper,
    accent: 'pink',
    description: '§4 — Tender, Events, Contract Management and Client/CRM.',
    subgroups: [
      { label: 'Tender', itemPaths: ['/tenders/dashboard', '/tenders'] },
      { label: 'Events', itemPaths: ['/events'] },
      { label: 'Contract Management', itemPaths: ['/contracts', '/mobilizations'] },
      { label: 'Clients', itemPaths: ['/masters/customers'] },
    ],
    items: [
      { label: 'Tender dashboard', to: '/tenders/dashboard', icon: CalendarClock, permission: 'tender.view', description: 'What needs action today.' },
      { label: 'All tenders', to: '/tenders', icon: FileWarning, permission: 'tender.view', description: 'The bid pipeline, Go/No-Go, eligibility and costing.' },
      { label: 'Events', to: '/events', icon: PartyPopper, permission: 'events.view', description: 'Event bookings, manpower, ODC, equipment and closure.' },
      { label: 'Contracts', to: '/contracts', icon: FileSignature, permission: 'contract.view', description: 'Client contracts, obligations and renewals.' },
      { label: 'Site mobilization', to: '/mobilizations', icon: Sparkles, permission: 'mobilization.view', description: 'Opening a new site, with a go-live gate.' },
      { label: 'Clients', to: '/masters/customers', icon: Contact, permission: 'masterdata.view', description: 'Client master, contacts and statements.' },
    ],
  },
  {
    label: 'Finance',
    icon: DollarSign,
    accent: 'indigo',
    description: '§4 — Billing & Collections, Finance and Asset Management.',
    subgroups: [
      { label: 'Billing & Collections', itemPaths: ['/billing/dashboard', '/billing/invoices', '/billing/credit-notes', '/billing/statements', '/reports/customer-aging'] },
      { label: 'Finance', itemPaths: ['/finance/cash-flow', '/finance/profit-vs-cash', '/finance/facilities', '/expenses/dashboard', '/accounting/dashboard', '/accounting/bank-accounts'] },
      { label: 'Asset Management', itemPaths: ['/assets'] },
    ],
    items: [
      { label: 'Billing', to: '/billing/dashboard', icon: BarChart3, permission: 'sales.view', description: 'Client invoices and receipts.' },
      { label: 'Invoices', to: '/billing/invoices', icon: ReceiptText, permission: 'sales.view', description: 'Every invoice raised.' },
      { label: 'Credit notes', to: '/billing/credit-notes', icon: BadgePercent, permission: 'sales.view', description: 'Credit and debit notes.' },
      { label: 'Client statements', to: '/billing/statements', icon: FileText, permission: 'sales.view', description: 'Client ledger statements.' },
      { label: 'Collections & ageing', to: '/reports/customer-aging', icon: ReceiptText, permission: 'reporting.view', description: 'Who owes what, for how long, and who is chasing it.' },

      { label: 'Cash flow', to: '/finance/cash-flow', icon: Wallet, permission: 'finance.view_cash_flow', description: 'What is coming in and going out, and the lowest point.' },
      { label: 'Profit vs cash', to: '/finance/profit-vs-cash', icon: TrendingUp, permission: 'finance.view_pnl', description: 'Which clients are earning us money and which are holding it.' },
      { label: 'Overdraft & loans', to: '/finance/facilities', icon: Landmark, permission: 'finance.view_od_loans', description: 'Overdrafts and loans, with headroom and interest.' },
      { label: 'Expenses', to: '/expenses/dashboard', icon: Banknote, permission: 'expense.view', description: 'Site and event expense claims.' },
      { label: 'Accounting', to: '/accounting/dashboard', icon: Calculator, permission: 'accounting.view', description: 'Journal, trial balance and ledger.' },
      { label: 'Bank accounts', to: '/accounting/bank-accounts', icon: Landmark, permission: 'accounting.view', description: 'Bank balances and reconciliation.' },

      { label: 'Assets & CAPEX', to: '/assets', icon: Wrench, permission: 'assets.view', description: 'Equipment register, CAPEX and depreciation.' },
    ],
  },
  {
    label: 'Quality & Upkeep',
    icon: CheckSquare,
    accent: 'rose',
    description: '§4 — Quality & Food Safety, and Maintenance.',
    subgroups: [
      { label: 'Quality & Food Safety', itemPaths: ['/quality/dashboard', '/quality/food-safety', '/quality/complaints', '/quality/ncr', '/compliance/dashboard'] },
      { label: 'Maintenance', itemPaths: ['/maintenance/requests'] },
    ],
    items: [
      { label: 'Quality overview', to: '/quality/dashboard', icon: CheckSquare, permission: 'quality.view', description: 'Inspections and corrective actions.' },
      { label: 'Food safety checks', to: '/quality/food-safety', icon: Thermometer, permission: 'quality.view', description: 'Temperatures, water tests and retained samples.' },
      { label: 'Complaints', to: '/quality/complaints', icon: MessageSquareWarning, permission: 'quality.view', description: 'What diners and clients said was wrong.' },
      { label: 'Corrective actions', to: '/quality/ncr', icon: FileWarning, permission: 'quality.view', description: 'Non-conformances and the action taken to close them.' },
      { label: 'Licences & audits', to: '/compliance/dashboard', icon: Book, permission: 'compliance.view', description: 'Licences, certificates and audits, with expiry.' },
      { label: 'Maintenance', to: '/maintenance/requests', icon: Wrench, permission: 'maintenance.view', description: 'Breakdowns and preventive schedules.' },
    ],
  },
  {
    label: 'MIS & Reports',
    icon: BarChart3,
    accent: 'cyan',
    description: '§48 — Business intelligence across every department.',
    items: [
      { label: 'Business health', to: '/reports/business-health', icon: Activity, permission: 'reporting.view', description: 'One page: what is good, what is bad.' },
      { label: 'Profit & Loss', to: '/reports/pnl', icon: DollarSign, permission: 'finance.view_pnl', description: 'Company and site-wise P&L.' },
      { label: 'Client ageing', to: '/reports/customer-aging', icon: ReceiptText, permission: 'reporting.view', description: 'Who owes what, for how long.' },
      { label: 'Billing register', to: '/reports/sales-register', icon: ReceiptText, permission: 'reporting.view', description: 'Every invoice for a period.' },
      { label: 'Purchase register', to: '/reports/purchase-register', icon: ReceiptText, permission: 'reporting.view', description: 'Every vendor bill for a period.' },
      { label: 'Stock valuation', to: '/reports/stock-valuation', icon: Boxes, permission: 'reporting.view', description: 'On-hand value by site.' },
      { label: 'Wastage report', to: '/reports/waste', icon: Trash2, permission: 'reporting.view', description: 'Wastage by stage and reason.' },
    ],
  },
  {
    label: 'WhatsApp',
    icon: MessageCircle,
    accent: 'emerald',
    items: [
      { label: 'Overview', to: '/whatsapp', icon: Gauge, module: 'whatsapp_marketing', description: 'Delivery, reply and campaign numbers at a glance.' },
      { label: 'Inbox', to: '/whatsapp/inbox', icon: MessageCircle, module: 'whatsapp_marketing', description: 'Two-way conversations with clients and vendors.' },
      { label: 'Contacts', to: '/whatsapp/contacts', icon: Contact, module: 'whatsapp_marketing', description: 'WhatsApp phonebook, synced from clients and staff.' },
      { label: 'Groups', to: '/whatsapp/groups', icon: Users, module: 'whatsapp_marketing', description: 'Contact groups a campaign can be sent to.' },
      { label: 'Templates', to: '/whatsapp/templates', icon: FileText, module: 'whatsapp_marketing', description: 'Meta-approved message templates.' },
      { label: 'Campaigns', to: '/whatsapp/campaigns', icon: Sparkles, module: 'whatsapp_marketing', description: 'Scheduled bulk sends with delivery tracking.' },
      { label: 'Reports', to: '/whatsapp/reports', icon: BarChart3, module: 'whatsapp_marketing', description: 'Sent, delivered, read and failed, by campaign.' },
      { label: 'Settings', to: '/whatsapp/settings', icon: Settings, module: 'whatsapp_marketing', description: 'Number, templates per document, and send method.' },
      { label: 'Connect a number', to: '/whatsapp/setup', icon: KeyRound, module: 'whatsapp_marketing', description: 'Link a WhatsApp Business number to this company.' },
    ],
  },
  {
    label: 'Administration',
    icon: Settings,
    accent: 'slate',
    description: '§4 — Administration.',
    items: [
      { label: 'Companies', to: '/setup/companies', icon: Building2, description: 'Legal entities.' },
      { label: 'Regions', to: '/setup/branches', icon: Building2, description: 'Operating regions.' },
      { label: 'Departments', to: '/setup/departments', icon: Building2, description: 'The departments the requirement lists.' },
      { label: 'Users & roles', to: '/setup/users', icon: Users, permission: 'security.manage_users', description: 'Login accounts and role permissions.' },
      { label: 'Stores', to: '/masters/warehouses', icon: Warehouse, permission: 'masterdata.view', description: 'Storage locations per site.' },
      { label: 'Categories', to: '/masters/categories', icon: Boxes, permission: 'masterdata.view', description: 'Material and party categories.' },
      { label: 'Taxes', to: '/masters/taxes', icon: BadgePercent, permission: 'masterdata.view', description: 'GST slabs and tax rates.' },
      { label: 'Units of measure', to: '/masters/uoms', icon: Boxes, permission: 'masterdata.view', description: 'Kilogram, litre, piece.' },
      { label: 'Payment terms', to: '/masters/payment-terms', icon: CalendarClock, permission: 'masterdata.view', description: 'Net 30, Net 45, immediate.' },
      { label: 'Currencies', to: '/masters/currencies', icon: DollarSign, permission: 'masterdata.view', description: 'Currency master.' },
      { label: 'Numbering', to: '/setup/numbering', icon: FileText, permission: 'security.manage_settings', description: 'Document number formats.' },
      { label: 'Global settings', to: '/setup/global-settings', icon: Settings, permission: 'security.manage_settings', description: 'Company-wide preferences.' },
      { label: 'Terminology', to: '/setup/terminology', icon: FileText, permission: 'security.manage_settings', description: 'Label overrides in your own words.' },
      { label: 'Import data', to: '/setup/import', icon: FileText, permission: 'security.import_data', description: 'CSV and Excel import with field mapping.' },
      { label: 'Audit log', to: '/setup/audit-log', icon: FileText, permission: 'security.view_audit_log', description: 'Who changed what and when.' },
    ],
  },
];

/** Flat list, for the command palette and quick-jump search. */
export const NAV_FLAT: NavItem[] = NAV.flatMap((g) => g.items);

/** Kept for compatibility with the shared sidebar/topbar that reference these types. */
export type NavAccent = 'sky' | 'emerald' | 'amber' | 'rose' | 'violet' | 'cyan' | 'teal' | 'indigo' | 'pink' | 'orange' | 'slate';
export interface NavSubgroup {
  label: string;
  items?: string[];
  itemPaths: string[];
  icon?: React.ComponentType<{ className?: string }>;
  description?: string;
}

/**
 * Filter a nav tree to what a given set of enabled modules can see.
 *
 * The signature accepts both a `Set<string>` and a `string[]` so the sidebar
 * (which passes a Set) and the tests (which pass an array) both work. A NULL
 * or undefined `module` on an item means "always on" (core routes).
 */
export function filterNav(
  tree: NavGroup[] = NAV,
  enabled: Set<string> | ReadonlyArray<string> | null | undefined = null,
  permissions: Set<string> | ReadonlyArray<string> | null | undefined = null,
): NavGroup[] {
  const set = enabled ? (enabled instanceof Set ? enabled : new Set(enabled)) : null;
  const perms = permissions ? (permissions instanceof Set ? permissions : new Set(permissions)) : null;

  return tree
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => isNavItemEnabled(i, set) && isNavItemPermitted(i, perms)),
    }))
    .filter((g) => g.items.length > 0);
}

/** Predicate used by tests and the sidebar to check per-item visibility. */
export function isNavItemEnabled(
  item: NavItem,
  enabled: Set<string> | ReadonlyArray<string> | null | undefined,
): boolean {
  if (!item.module) return true;
  if (!enabled) return true;
  const set = enabled instanceof Set ? enabled : new Set(enabled);
  return set.has(item.module);
}

/**
 * Does this user hold the permission an item declares?
 *
 * A null permission set means "do not filter" — used before `/me` has
 * resolved, so the sidebar does not flicker empty on first paint. Hiding is a
 * convenience for the person reading the menu; the endpoint behind every item
 * is gated server-side regardless.
 */
export function isNavItemPermitted(
  item: NavItem,
  permissions: Set<string> | ReadonlyArray<string> | null | undefined,
): boolean {
  if (!item.permission) return true;
  if (!permissions) return true;
  const set = permissions instanceof Set ? permissions : new Set(permissions);
  return set.has(item.permission);
}
